import React, { useEffect, useRef, useState } from 'react';
import { Camera, Flag, Loader2, X, Send, Users, Heart, MessageCircle, Trash2 } from 'lucide-react';
import Lightbox from './Lightbox';
import SmartImage from './SmartImage';
import api, { mediaUrl, uploadImage, errText } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

export const EventCommunityPhotos = ({ slug }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [canPost, setCanPost] = useState(false);
  const [pendingImg, setPendingImg] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [reported, setReported] = useState({});
  const [openId, setOpenId] = useState(null);
  const [comments, setComments] = useState([]);
  const [cText, setCText] = useState('');
  const [cBusy, setCBusy] = useState(false);
  const [cReported, setCReported] = useState({});

  const toggleReact = async (ph) => {
    if (!user) { toast({ title: 'Please sign in to react' }); return; }
    try {
      const res = await api.post(`/event-photos/${ph.id}/react`);
      setPhotos((prev) => prev.map((p) => (p.id === ph.id ? { ...p, liked: res.data.liked, like_count: res.data.like_count } : p)));
    } catch (e) { toast({ title: 'Failed', description: errText(e) }); }
  };

  const openComments = async (ph) => {
    if (openId === ph.id) { setOpenId(null); return; }
    setOpenId(ph.id); setComments([]);
    try { const r = await api.get(`/event-photos/${ph.id}/comments`); setComments(r.data || []); }
    catch { setComments([]); }
  };

  const addComment = async () => {
    if (!cText.trim() || !openId) return;
    setCBusy(true);
    try {
      const r = await api.post(`/event-photos/${openId}/comments`, { text: cText.trim() });
      setComments((prev) => [...prev, r.data]);
      setPhotos((prev) => prev.map((p) => (p.id === openId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p)));
      setCText('');
    } catch (e) { toast({ title: 'Could not comment', description: errText(e) }); }
    finally { setCBusy(false); }
  };

  const reportComment = async (cid) => {
    setCReported((r) => ({ ...r, [cid]: true }));
    try { await api.post(`/event-photos/comments/${cid}/report`); toast({ title: 'Reported', description: 'Thank you — our team will review it.' }); }
    catch (e) { toast({ title: 'Failed', description: errText(e) }); }
  };

  const delComment = async (cid) => {
    try { await api.delete(`/event-photos/comments/${cid}`); setComments((prev) => prev.filter((c) => c.id !== cid)); }
    catch (e) { toast({ title: 'Failed', description: errText(e) }); }
  };

  const load = () => {
    api.get(`/programs/${slug}/photos`).then((r) => setPhotos(r.data || [])).catch(() => setPhotos([]));
  };
  useEffect(() => {
    load();
    if (user) api.get(`/programs/${slug}/photos/can-post`).then((r) => setCanPost(!!r.data?.can_post)).catch(() => setCanPost(false));
  }, [slug, user]); // eslint-disable-line

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try { setPendingImg(await uploadImage(file)); }
    catch (e) { toast({ title: 'Upload failed', description: errText(e, 'Please try a different image.') }); }
    finally { setUploading(false); }
  };

  const share = async () => {
    if (!pendingImg) return;
    setSaving(true);
    try {
      const res = await api.post(`/programs/${slug}/photos`, { image_url: pendingImg, caption });
      setPhotos((prev) => [res.data, ...prev]);
      setPendingImg(''); setCaption('');
      toast({ title: 'Thanks for sharing! 🌸', description: 'Your photo is now on the event page.' });
    } catch (e) { toast({ title: 'Could not share', description: errText(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const report = async (id) => {
    setReported((r) => ({ ...r, [id]: true }));
    try { await api.post(`/event-photos/${id}/report`); toast({ title: 'Reported', description: 'Thank you — our team will review it.' }); }
    catch (e) { toast({ title: 'Failed', description: errText(e) }); }
  };

  const removeOwn = async (id) => {
    if (!window.confirm('Remove your photo?')) return;
    try { await api.delete(`/event-photos/${id}`); setPhotos((prev) => prev.filter((p) => p.id !== id)); }
    catch (e) { toast({ title: 'Failed', description: errText(e) }); }
  };

  if (!photos.length && !canPost) return null;

  return (
    <div data-testid="event-community-photos">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-5 h-5 text-rose-italic" />
        <h2 className="font-serif-display text-2xl text-burgundy">From our community</h2>
      </div>
      <p className="text-sm text-mahogany/70 mb-4">Moments shared by people who attended.</p>

      {canPost && (
        <div className="mb-6 p-4 rounded-2xl bg-white/70 border border-rose/50" data-testid="ecp-composer">
          {pendingImg ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <img src={mediaUrl(pendingImg)} alt="" className="h-28 w-28 object-cover rounded-xl border border-rose/40" />
              <div className="flex-1">
                <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={280} data-testid="ecp-caption"
                  placeholder="Add a short caption (optional)…" className="w-full bg-cream rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                <div className="mt-2 flex gap-2">
                  <button onClick={share} disabled={saving} data-testid="ecp-share"
                    className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Share photo
                  </button>
                  <button onClick={() => { setPendingImg(''); setCaption(''); }} className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
                </div>
              </div>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 pill-btn cursor-pointer" data-testid="ecp-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Share your photos
              <input ref={fileRef} type="file" accept="image/*,.heic,.heif" className="hidden" data-testid="ecp-file-input"
                onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
          )}
        </div>
      )}

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((ph, i) => (
            <div key={ph.id} className="group relative rounded-xl overflow-hidden bg-white/60 border border-rose/40" data-testid={`ecp-photo-${ph.id}`}>
              <button type="button" onClick={() => setLightboxIdx(i)} className="block w-full">
                <SmartImage src={mediaUrl(ph.image_url)} alt={ph.caption} className="aspect-square transition-transform duration-300 group-hover:scale-105" />
              </button>
              <div className="p-2">
                {ph.caption && <p className="text-xs text-burgundy/90 line-clamp-2">{ph.caption}</p>}
                <p className="text-[11px] text-mahogany/60 mt-0.5">— {ph.user_name}</p>
                <div className="mt-1.5 flex items-center gap-3 text-mahogany/70">
                  <button onClick={() => toggleReact(ph)} data-testid={`ecp-like-${ph.id}`}
                    className={`inline-flex items-center gap-1 text-xs transition-colors ${ph.liked ? 'text-rose-italic' : 'hover:text-rose-italic'}`}>
                    <Heart className={`w-4 h-4 ${ph.liked ? 'fill-current' : ''}`} /> {ph.like_count || 0}
                  </button>
                  <button onClick={() => openComments(ph)} data-testid={`ecp-comments-${ph.id}`}
                    className="inline-flex items-center gap-1 text-xs hover:text-burgundy transition-colors">
                    <MessageCircle className="w-4 h-4" /> {ph.comment_count || 0}
                  </button>
                </div>
                {openId === ph.id && (
                  <div className="mt-2 border-t border-rose/30 pt-2 space-y-2" data-testid={`ecp-comment-thread-${ph.id}`}>
                    {comments.length === 0 && <p className="text-[11px] text-mahogany/50">No comments yet.</p>}
                    {comments.map((c) => (
                      <div key={c.id} className="text-xs group/c">
                        <span className="text-burgundy font-medium">{c.user_name}</span>{' '}
                        <span className="text-burgundy/80">{c.text}</span>
                        {user && (c.user_id === user.user_id ? (
                          <button onClick={() => delComment(c.id)} data-testid={`ecp-comment-del-${c.id}`} className="ml-1 text-mahogany/40 hover:text-red-600"><Trash2 className="inline w-3 h-3" /></button>
                        ) : (
                          <button onClick={() => reportComment(c.id)} disabled={cReported[c.id]} data-testid={`ecp-comment-report-${c.id}`} className="ml-1 text-mahogany/40 hover:text-red-600 disabled:text-red-500"><Flag className="inline w-3 h-3" /></button>
                        ))}
                      </div>
                    ))}
                    {user && (
                      <div className="flex items-center gap-1.5 pt-1">
                        <input value={cText} onChange={(e) => setCText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addComment()}
                          maxLength={500} placeholder="Add a comment…" data-testid={`ecp-comment-input-${ph.id}`}
                          className="flex-1 bg-cream rounded-full border border-rose/50 px-3 py-1 text-xs text-burgundy focus:outline-none focus:border-mahogany" />
                        <button onClick={addComment} disabled={cBusy} data-testid={`ecp-comment-send-${ph.id}`}
                          className="w-7 h-7 rounded-full bg-burgundy text-cream flex items-center justify-center disabled:opacity-60">
                          {cBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {user && (
                ph.user_id === user.user_id ? (
                  <button onClick={() => removeOwn(ph.id)} data-testid={`ecp-delete-${ph.id}`}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remove your photo"><X className="w-3.5 h-3.5" /></button>
                ) : (
                  <button onClick={() => report(ph.id)} disabled={reported[ph.id]} data-testid={`ecp-report-${ph.id}`}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100 disabled:bg-red-600/70" title="Report this photo"><Flag className="w-3.5 h-3.5" /></button>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {lightboxIdx !== null && (
        <Lightbox images={photos.map((p) => mediaUrl(p.image_url))} index={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </div>
  );
};

export default EventCommunityPhotos;
